import React, { useState } from "react";
import { getName } from "country-list";
import { Link } from "react-router-dom";
import { useClubs, useCourts, useDeleteClub } from "../apiCalls/clubApiCalls";
import {
  Badge,
  Button,
  Card,
  Cell,
  EmptyState,
  ErrorState,
  Loader,
  PageHeader,
  Row,
  Table,
} from "../components/ui";
import ConfirmDelete from "../components/ConfirmDelete";
import ClubForm from "./ClubForm";

const HEAD = [
  { label: "Club" },
  { label: "Location" },
  { label: "Listing" },
  { label: "Courts", align: "right" },
  { label: "", key: "actions", align: "right" },
];

export default function Clubs() {
  const [editing, setEditing] = useState(null); // club | "new" | null
  const [deleting, setDeleting] = useState(null);

  const clubs = useClubs();
  // One fetch of every court, counted per club — cheaper than a request per
  // row, and the totals are small.
  const courts = useCourts();
  const { mutate: remove, isLoading: isDeleting } = useDeleteClub();

  const courtCount = (clubId) =>
    (courts.data || []).filter((court) => String(court.club?._id) === String(clubId))
      .length;

  return (
    <>
      <PageHeader
        title="Clubs & courts"
        subtitle="Venues and the courts at each one. The scheduler assigns matches to these courts."
        actions={
          <Button variant="primary" onClick={() => setEditing("new")}>
            Add club
          </Button>
        }
      />

      <Card>
        {clubs.isLoading ? (
          <Loader label="Loading clubs" />
        ) : clubs.isError ? (
          <ErrorState message="Couldn't load clubs." onRetry={clubs.refetch} />
        ) : (clubs.data || []).length === 0 ? (
          <EmptyState
            message="No clubs yet. Add a venue before you schedule anything at it."
            action={
              <Button variant="primary" onClick={() => setEditing("new")}>
                Add the first club
              </Button>
            }
          />
        ) : (
          <Table head={HEAD}>
            {clubs.data.map((club) => (
              <Row key={club._id}>
                <Cell>
                  <Link
                    to={`/clubs/${club._id}`}
                    className="font-medium underline decoration-sand underline-offset-4 hover:decoration-teal"
                  >
                    {club.name}
                  </Link>
                  <div className="font-mono text-xs text-ink-soft">
                    /{club.slug}
                  </div>
                </Cell>
                <Cell>{[club.city, getName(club.country) || club.country].filter(Boolean).join(", ")}</Cell>
                <Cell>
                  <Badge tone={club.isActive ? "active" : "neutral"}>
                    {club.isActive ? "Listed" : "Retired"}
                  </Badge>
                </Cell>
                <Cell align="right" className="font-mono tabular">
                  {courts.isLoading ? "—" : courtCount(club._id)}
                </Cell>
                <Cell align="right">
                  <div className="flex justify-end gap-2">
                    <Button
                      className="px-3 py-1.5 text-xs"
                      onClick={() => setEditing(club)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      className="px-3 py-1.5 text-xs"
                      onClick={() => setDeleting(club)}
                    >
                      Delete
                    </Button>
                  </div>
                </Cell>
              </Row>
            ))}
          </Table>
        )}
      </Card>

      {editing && (
        <ClubForm
          club={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      {deleting && (
        <ConfirmDelete
          title={`Delete ${deleting.name}?`}
          body={`This deletes the club and all ${courtCount(
            deleting._id
          )} of its courts. Players affiliated to it become independent.`}
          isDeleting={isDeleting}
          onConfirm={() =>
            remove(deleting._id, { onSuccess: () => setDeleting(null) })
          }
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
}
